import { FilterBar, SearchField } from "@/components/filter-bar";
import FilterChips from "@/components/filter-chips";
import { Button } from "@buildpy/ui/button";
import { SimpleSelect } from "@buildpy/ui/select";
import {
  CUSTOMER_STAGE_LABEL,
  CUSTOMER_STAGES,
  CustomerStage,
} from "@buildpy/core/modules/customer/types";
import { Member } from "@buildpy/core/modules/org/types";

export interface CustomerFilterState {
  q: string;
  stage: CustomerStage | "";
  assignee: string;
}

type Props = {
  filter: CustomerFilterState;
  members: Pick<Member, "id" | "name">[];
} & (
  | { view?: null; counts: Record<CustomerStage, number>; total: number; col?: never; all?: never }
  // 보드는 열이 곧 단계라 단계 칩이 없다 — 폰 열 탭(col)·펼친 열(all)은 검색 뒤에도 남긴다.
  | { view: "board"; counts?: never; total?: never; col?: string | null; all?: string | null }
);

const hrefWith = (filter: CustomerFilterState, view: "board" | null = null): string => {
  const params = new URLSearchParams();
  if (view) params.set("view", view);
  if (filter.q) params.set("q", filter.q);
  if (filter.stage) params.set("stage", filter.stage);
  if (filter.assignee) params.set("assignee", filter.assignee);
  const query = params.toString();
  return query ? `/app/customers?${query}` : "/app/customers";
};

/**
 * 검색·단계·담당자.
 *
 * 전부 URL 쿼리다 — 걸어 둔 조건이 새로고침·뒤로가기에 살아남고, 그 화면을
 * 그대로 남에게 보낼 수 있다. 검색은 폼 submit(GET)이다. 담당자 드롭다운만은
 * 자바스크립트가 있어야 고를 수 있다(값은 숨은 input 으로 같이 간다).
 *
 * 단계는 드롭다운이 아니라 칩이다. 건수를 같이 보여 주려고 — 열어 보지 않아도
 * 어디에 몇 건이 쌓여 있는지 보인다.
 */
const CustomerFilters = ({ filter, members, view = null, counts, total, col, all }: Props) => (
  <div className="flex flex-col gap-y-3">
    <FilterBar
      action="/app/customers"
      filtered={Boolean(filter.q || filter.assignee || (!view && filter.stage))}
      resetHref={hrefWith({ q: "", stage: "", assignee: "" }, view)}>
      {view && <input name="view" type="hidden" value={view} />}
      {view && col && <input name="col" type="hidden" value={col} />}
      {view && all && <input name="all" type="hidden" value={all} />}
      {!view && filter.stage && (
        <input name="stage" type="hidden" value={filter.stage} />
      )}

      <SearchField
        aria-label="고객 검색"
        defaultValue={filter.q}
        name="q"
        placeholder="이름 · 전화 · 현장 주소"
      />

      {/* URL 의 담당자만 바뀌는 이동(초기화·단계 칩·뒤로가기)에도 옛 선택이 남지 않게 다시 그린다. */}
      <SimpleSelect
        key={filter.assignee}
        aria-label="담당자"
        className="w-auto min-w-32"
        defaultValue={filter.assignee}
        name="assignee"
        options={[
          { value: "", label: "담당자 전체" },
          ...members.map((member) => ({ value: member.id, label: member.name })),
        ]}
        size="sm"
      />

      <Button size="sm" type="submit" variant="outline">
        검색
      </Button>
    </FilterBar>

    {counts && (
      <FilterChips
        activeHref={hrefWith(filter)}
        aria-label="고객 단계"
        items={[
          { label: "전체", href: hrefWith({ ...filter, stage: "" }), count: total },
          ...CUSTOMER_STAGES.map((stage) => ({
            label: CUSTOMER_STAGE_LABEL[stage],
            href: hrefWith({ ...filter, stage }),
            count: counts[stage],
          })),
        ]}
      />
    )}
  </div>
);

export default CustomerFilters;
